/**
 * Card de decisão — Ação Recomendada (cockpit Ao Vivo).
 */
import React from 'react';
import { Target } from 'lucide-react';
import { EXEC_HERO_CLASS } from './opsExecutiveShared';

interface NextAction {
  label: string;
  reason: string;
  impactoEstimado?: string;
  severity: 'critical' | 'warning';
}

interface ActionImpactMetrics {
  affectedSector: string | null;
  recoveryPoints: number;
  queueMinutes: number;
  delayLabel: string | null;
}

interface OpsRecommendedActionCardProps {
  title: string;
  nextAction: NextAction;
  impactMetrics: ActionImpactMetrics;
  responsible: string | null;
  status: string;
  deadline: string | null;
  primaryLabel: string;
  secondaryLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
  tertiaryLabel?: string;
  onTertiary?: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  open: 'Aberto',
  pending: 'Pendente',
  in_progress: 'Em andamento',
  validation: 'Em validação',
  monitoring: 'Em monitoramento',
  closed: 'Encerrado',
  blocked: 'Bloqueada',
  done: 'Concluída',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

function formatDeadline(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' });
}

function MetricRow({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-[var(--border-default)]/40 py-1.5 last:border-b-0">
      <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">{label}</span>
      <span className={`truncate text-right text-[11px] font-semibold ${accent ?? 'text-[var(--text-primary)]'}`}>
        {value}
      </span>
    </div>
  );
}

export const OpsRecommendedActionCard: React.FC<OpsRecommendedActionCardProps> = ({
  title,
  nextAction,
  impactMetrics,
  responsible,
  status,
  deadline,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  tertiaryLabel,
  onTertiary,
}) => {
  const isCritical = nextAction.severity === 'critical';
  const statusLabel = STATUS_LABEL[status] ?? status;

  return (
    <div className={`${EXEC_HERO_CLASS} flex min-h-0 flex-col border-l-[3px] ${isCritical ? 'border-l-red-500' : 'border-l-amber-500'}`}>
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/60 px-3 py-2">
        <Target size={14} className={isCritical ? 'text-red-500' : 'text-amber-600'} strokeWidth={2.25} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100">
          Ação Recomendada
        </span>
        <span className={`ml-auto rounded-sm px-1.5 py-0.5 text-[8px] font-bold uppercase ${isCritical ? 'bg-red-500/15 text-red-600' : 'bg-amber-500/15 text-amber-700'}`}>
          {isCritical ? 'Crítico' : 'Prioritário'}
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col px-3 py-2">
        <h3 className="line-clamp-2 text-[13px] font-bold leading-snug text-[var(--text-primary)]">{title}</h3>

        <div className="mt-2 grid grid-cols-2 gap-x-4">
          <MetricRow
            label="Tempo acumulado"
            value={impactMetrics.delayLabel ?? '—'}
            accent={impactMetrics.queueMinutes > 120 ? 'text-red-500' : 'text-amber-600'}
          />
          <MetricRow label="Impacto operacional" value={nextAction.reason} accent="text-red-600/90 dark:text-red-400" />
          <MetricRow label="Setor afetado" value={impactMetrics.affectedSector ?? 'Operação geral'} />
          <MetricRow label="Responsável" value={responsible?.trim() || 'Não atribuído'} />
          <MetricRow label="Status" value={statusLabel} />
          <MetricRow label="Prazo" value={formatDeadline(deadline)} accent={deadline ? 'text-red-500' : undefined} />
        </div>

        <div className="mt-auto flex shrink-0 flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={onPrimary}
            className={`rounded-[3px] px-3 py-1.5 text-[10px] font-bold text-white shadow-sm ${
              isCritical ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {primaryLabel}
          </button>
          <button
            type="button"
            onClick={onSecondary}
            className="rounded-[3px] border border-[var(--border-default)] px-3 py-1.5 text-[10px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--button-hover)]"
          >
            {secondaryLabel}
          </button>
          {tertiaryLabel && onTertiary ? (
            <button
              type="button"
              onClick={onTertiary}
              className="rounded-[3px] px-2 py-1.5 text-[10px] font-semibold text-[var(--nav-active-bar)]"
            >
              {tertiaryLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OpsRecommendedActionCard;
